import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";

import { zeosDialog } from "../components/Shared/confirm-dialog/ConfirmDialog";
import { ZeosButton } from "../components/Shared/ZeosButton";
import { ZeosPasswordInput } from "../components/Shared/ZeosPasswordInput";
import { AuthContext } from "../context/AuthContext";
import { ONBOARDING_BASE_PATH } from "../services/nav/Constants";
import { getSearchParamTyped } from "../services/nav/QueryStringHelpers";
import { AnonymousLayout } from "./AnonymousTemplate";

export const ResetPassword = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source =
    (searchParams.get("source") as "onboarding" | "dashboard") || "dashboard";
  const email = searchParams.get("email") || "";
  const mode = getSearchParamTyped<string>("mode", searchParams);
  const oobCode = getSearchParamTyped<string>("oobCode", searchParams);

  const [confirmPassword, setConfirmPassword] = useState("");
  const [formStatus, setFormStatus] = useState("Please wait");
  const [hasValidToken, setHasValidToken] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const resetPasswordSchema = Yup.object({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters.")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords do not match.")
      .required("Please confirm your password"),
  });

  useEffect(() => {
    const initAsync = async () => {
      if (oobCode == "" || mode == null) {
        setHasValidToken(false);
        setFormStatus(
          "Please request a password reset link on the sign-in page."
        );
      } else {
        //TODO: THIS SHOULD BE IN THE AUTH SERVICE/CONTEXT
        verifyPasswordResetCode(auth.directAuth, oobCode)
          .then((email) => {
            // setCodeOk(true);
            // setShowForm(true);
            setHasValidToken(true);
          })
          .catch((err) => {
            // setShowForm(false);
            // // console.log("catch", err);
            // // setError(`verifyPasswordResetCode: ${(err as Error).message}`);
            // //TRAP FOR SPECIFIC ERRORS AND INFORM USER
            setFormStatus(
              "Your password reset code is expired or invalid. Please request a new password reset link."
            );
          });
      }
    };
    initAsync();
  }, []);

  const validatePasswords = async () => {
    try {
      await resetPasswordSchema.validate(
        { password, confirmPassword },
        { abortEarly: false }
      );
      setPasswordError("");
      return true;
    } catch (err: any) {
      if (err.inner?.length > 0) {
        setPasswordError(err.inner[0].message);
      } else {
        setPasswordError(err.message);
      }
      return false;
    }
  };

  const handleSubmit = async () => {
    if (await validatePasswords()) {
      //TODO: THIS SHOULD BE IN THE AUTH SERVICE/CONTEXT
      try {
        confirmPasswordReset(auth.directAuth, oobCode, password)
          .then((resp) => {
            zeosDialog("Your password has been reset.");
            if (source === "onboarding") {
              navigate(
                `${ONBOARDING_BASE_PATH}/company?email=${encodeURIComponent(
                  email
                )}`
              );
            } else {
              navigate(`/login?email=${encodeURIComponent(email)}`);
            }
          })
          .catch((err) => {
            // Error occurred during confirmation. The code might have expired or the
            // password is too weak.
            // console.log("catch", err);
            // setError(`confirmPasswordReset: ${(err as Error).message}`);
            zeosDialog(
              "There was an error processing your reset request. Please use the sign-in page to request a new password reset link or to contact ZEOS."
            );
          });
        setFormStatus("Please wait");
      } catch (err) {
        // setError(`handleSubmit: ${(err as Error).message}`);
        zeosDialog(
          "There was an error processing your reset request. Please use the sign-in page to request a new password reset link or to contact ZEOS."
        );
      }
    }
  };

  const resetForm = (
    <div className="col-span-12 sm:col-span-8 md:col-span-6 mx-auto space-y-6">
      <ZeosPasswordInput
        name="password"
        label="Password"
        placeholder="Enter a password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={passwordError}
      />
      <ZeosPasswordInput
        name="confirmPassword"
        label="Confirm Password"
        placeholder="Re-enter password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={passwordError}
      />
      <div className="flex justify-between pt-4">
        <ZeosButton
          label="Submit"
          type="button"
          mode="next"
          onPress={handleSubmit}
        />
      </div>
    </div>
  );

  const renderContent = () => {
    if (!hasValidToken) {
      return (
        <div className="col-span-12 text-center text-zeos-red p-4">
          <p className="text-xl font-semibold">{formStatus}</p>
        </div>
      );
    }
    return resetForm;
  };

  return (
    <AnonymousLayout
      title="Reset Your Password"
      message="Please enter and confirm your new password to continue."
      content={renderContent()}
      contentType="form"
    />
  );
};
